import { useEffect, useState } from 'react';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { adminService, type RegistrationCheck } from '@/services/admin';

const selectClassName =
  'w-full rounded-lg border border-slate-300 px-3 py-2 text-base sm:text-sm min-h-[40px] shadow-sm focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/30';

type CheckState =
  | { kind: 'short' }
  | { kind: 'theirs' }
  | { kind: 'loading' }
  | { kind: 'available' }
  | { kind: 'taken'; left: boolean; firstName: string; lastName: string; className?: string | null }
  | { kind: 'error'; message: string };

type ChangeRegistrationDialogProps = {
  isOpen: boolean;
  currentNumber: string;
  ignoreStudentId?: string | null;
  pending?: boolean;
  onClose: () => void;
  onApply: (nextNumber: string) => Promise<void> | void;
};

const digitsOnly = (value: string) => value.replace(/\D/g, '');

export function ChangeRegistrationDialog({
  isOpen,
  currentNumber,
  ignoreStudentId,
  pending,
  onClose,
  onApply,
}: ChangeRegistrationDialogProps) {
  const [newNumber, setNewNumber] = useState('');
  const [nextUnused, setNextUnused] = useState('');
  const [check, setCheck] = useState<CheckState>({ kind: 'short' });
  const [applyError, setApplyError] = useState('');

  useEffect(() => {
    if (!isOpen) return;
    setNewNumber('');
    setCheck({ kind: 'short' });
    setApplyError('');
    adminService
      .nextRegistration()
      .then((data) => setNextUnused(data.registration || ''))
      .catch(() => setNextUnused(''));
  }, [isOpen, currentNumber]);

  useEffect(() => {
    if (!isOpen) return;
    const digits = digitsOnly(newNumber);
    if (digits.length < 3) {
      setCheck({ kind: 'short' });
      return;
    }
    if (digits === currentNumber) {
      setCheck({ kind: 'theirs' });
      return;
    }

    setCheck({ kind: 'loading' });
    const timer = window.setTimeout(async () => {
      try {
        const data: RegistrationCheck = await adminService.checkRegistration(digits, ignoreStudentId);
        if (data.available) {
          setCheck({ kind: 'available' });
          return;
        }
        setCheck({
          kind: 'taken',
          left: Boolean(data.occupant?.left),
          firstName: data.occupant?.firstName || '',
          lastName: data.occupant?.lastName || '',
          className: data.occupant?.className,
        });
      } catch {
        setCheck({ kind: 'error', message: 'Unable to check that number.' });
      }
    }, 250);

    return () => window.clearTimeout(timer);
  }, [newNumber, currentNumber, ignoreStudentId, isOpen]);

  const digits = digitsOnly(newNumber);
  const available = check.kind === 'available';

  const statusText = () => {
    if (check.kind === 'short') return 'Keep typing';
    if (check.kind === 'theirs') return 'This is already theirs';
    if (check.kind === 'loading') return 'Checking…';
    if (check.kind === 'available') return 'Available';
    if (check.kind === 'error') return check.message;
    if (check.left) return `In use — ${check.firstName} ${check.lastName} · left`;
    return `In use — ${check.firstName} ${check.lastName} · ${check.className || 'Unassigned'}`;
  };

  const handleApply = async () => {
    if (!available) return;
    setApplyError('');
    try {
      await onApply(digits);
    } catch (error) {
      const message =
        error && typeof error === 'object' && 'response' in error
          ? (error as { response?: { data?: { errors?: { rollNumber?: string[] }; message?: string } } }).response
              ?.data?.errors?.rollNumber?.[0] ??
            (error as { response?: { data?: { message?: string } } }).response?.data?.message
          : null;
      setApplyError(message || 'Unable to change that registration.');
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Change registration"
      zClassName="z-[60]"
      panelClassName="max-w-md"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={pending}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleApply} disabled={!available || pending || !digits}>
            {pending ? 'Saving…' : `Change ${currentNumber || '—'} → ${digits || '—'}`}
          </Button>
        </>
      }
    >
      <p className="text-sm text-slate-600">
        Current: <span className="font-medium text-slate-900">{currentNumber || '—'}</span>
      </p>
      <label htmlFor="newRegistration" className="mb-1 mt-4 block text-sm font-medium text-slate-700">
        New number
      </label>
      <input
        id="newRegistration"
        type="text"
        inputMode="numeric"
        autoFocus
        className={selectClassName}
        value={newNumber}
        onChange={(event) => setNewNumber(digitsOnly(event.target.value))}
      />
      <p
        className={`mt-2 text-sm ${
          check.kind === 'available'
            ? 'text-emerald-700'
            : check.kind === 'taken' || check.kind === 'error'
              ? 'text-rose-700'
              : 'text-slate-500'
        }`}
      >
        {statusText()}
      </p>
      {applyError ? <p className="mt-2 text-sm text-rose-700">{applyError}</p> : null}
      <div className="mt-4 flex items-center justify-between gap-3 rounded-lg bg-slate-50 px-3 py-2">
        <p className="text-sm text-slate-600">
          Next unused: <span className="font-medium text-slate-900">{nextUnused || '—'}</span>
        </p>
        <Button
          variant="ghost"
          className="px-2 py-1 text-xs"
          disabled={!nextUnused}
          onClick={() => setNewNumber(nextUnused)}
        >
          Use next unused
        </Button>
      </div>
    </Modal>
  );
}
